﻿import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { FormCheckbox, FormDateRangeInput, FormLocationSelector, FormTextInput, FormDateInput } from 'components/form-fields';
import { Button, ButtonSave } from 'components/buttons'
import { ListingTable } from "components/listing";
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { JobService } from 'services';
import { Collapsable, ShowHide } from "components/layout";
import { Filter, FilterButton } from 'components/filtering';
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { DateToLocal } from "components/dates";
import { NotificationToaster, AlertUnsavedData } from "components/notifications";
import { Tooltip } from 'components/tooltip';
import { Intent, Position, Icon } from '@blueprintjs/core';
import { CancelJob, BulkEditJob } from 'pages/job/edit/shared';
import './JobListing.css';
import { HelperFunctions, JobHelperFunctions } from 'helpers';
import PropTypes from 'prop-types';
import moment from "moment";
import { Link } from "react-router-dom";

export function JobListingContract(
    {
        loading = false,
        filters,
        locations,
        reload,
        reloadDone,
        reloadRequired,
        requiredAction
    }
) {

    const debounceTimeout = 750;

    const [contracts, setContracts] = useState([]);
    const [hasAppliedSavedState, setHasAppliedSavedState] = useState(false);
    const expandedStateRef = useRef({});
    const [loadingData, setLoadingData] = useState(true);
    const [locationSetUp, setLocationSetUp] = useState(false);
    const [dateSetUp, setDateSetUp] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [locationListCV, setLocationListCV] = useState({ location: [], hierarchy: [] });
    const [bulkEditlocationList, setBulkEditLocationList] = useState({ location: [], hierarchy: [] });
    const searchInputRef = useRef(null);
    const canAccessPage = UserHasAccess(requiredAction);
    const noDataMessage = "No visits found.";
    const allJobs = "All Jobs";
    const dateFilterReduxName = "jobListingDatesContract";
    const jobLocationReduxName = "jobListingLocationContract";
    const jobSearchReduxName = "jobSearcContract";
    const statusColumn = 5;


    const reduxDates = useSelector(state => state.filters[dateFilterReduxName]);
    const reduxLocations = useSelector(state => state.filters[jobLocationReduxName]);
    const reduxSearch = useSelector(state => state.filters[jobSearchReduxName]);
    const dispatch = useDispatch();

    const [selectedLocations, setSelectedLocations] = useState([]);
    const locationListOnFilterOpen = useRef(selectedLocations);
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentFilters = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentLocationList = useRef();
    const [selectedJobsToEdit, setSelectedJobsToEdit] = useState([]);
    const [selectedJobsToCancel, setSelectedJobsToCancel] = useState([]);
    const [selectedContractLines, setSelectedContractLines] = useState([]);
    const [selectedContractLinesToCancel, setSelectedContractLinesToCancel] = useState([]);
    const selectedContractLinesIdPrefix = "checkbox-selectall-";
    const selectedContractLinesToCancelIdPrefix = "checkbox-selectall-to-cancel-";
    const selectedJobIdPrefix = "checkbox-";
    const [jobsByContract, setJobsByContract] = useState([]);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);

    const defaultFilters = [
        { name: "UnallocatedJobs", value: allJobs, displayName: allJobs }
    ];
    const [updatedJobs, setUpdatedJobs] = useState([]);
    const [saving, setSaving] = useState(false);

    const refOverflowTable = useRef();
    const refHeaderTable = useRef();
    const refHeaderScrollListener = useRef();

    useEffect(() => {
        setUpPage();
        setUpFilters();
        
        // Restore expanded state when component mounts
        restoreExpandedState();
    }, []);

    // Restore expanded state from sessionStorage
    function restoreExpandedState() {
        const savedState = sessionStorage.getItem('contractsExpandedState');
        if (savedState) {
            return JSON.parse(savedState);
        }
        return null;
    }

    // Save expanded state when job links are clicked
    function handleJobLinkClick(jobId) {
        // Save current expanded state using ref data for accuracy
        const expandedState = {
            contracts: contracts.map(contract => {
                const refData = expandedStateRef.current[contract.contractId] || {};
                return {
                    contractId: contract.contractId,
                    expanded: refData.expanded || false,
                    contractLines: contract.contractLines.map(cl => ({
                        contractLineId: cl.contractLineId,
                        expanded: (refData.contractLines && refData.contractLines[cl.contractLineId]) || false
                    }))
                };
            })
        };
        sessionStorage.setItem('contractsExpandedState', JSON.stringify(expandedState));
        
        return true; // Allow navigation to continue
    }

    useEffect(() => {
        mapContractRows(jobsByContract, selectedJobsToEdit, selectedJobsToCancel);
    }, [jobsByContract, selectedJobsToEdit, selectedJobsToCancel, updatedJobs]);

    useEffect(() => {
        if (reload) {
            loadContractScreenData();
        }
    }, [reload]);

    useEffect(() => {
        //This will trigger if the data changes, but will only process if the loadingData is set to true.
        if (loadingData && canAccessPage && 
            ((startDate && endDate) || (!startDate && !endDate)) &&
            locationSetUp && dateSetUp && selectedLocations != null) {

            let filtersArray = getFilters();
            var locationlist = filtersArray.filter(cf => cf.key === 'Location').map(cf => cf.value).toString();
            var loadingValue = false;
            if (
                currentSearchTerm.current !== searchTerm ||
                currentFilters.current !== filtersArray.length ||
                currentStartDate.current !== startDate ||
                currentEndDate.current !== endDate ||
                currentLocationList.current !== locationlist
            ) {
                loadingValue = true;

                currentSearchTerm.current = searchTerm;
                currentFilters.current = filtersArray.length;
                currentStartDate.current = startDate;
                currentEndDate.current = endDate;
                currentLocationList.current = locationlist;

                loadContractScreenData();
            }

            setLoadingData(loadingValue);
            focusOnTextBox(searchInputRef);
        }

    }, [loadingData, appliedFilters, selectedLocations]);

    useEffect(() => {
        if (debouncedSearchTerm !== null && debouncedSearchTerm !== currentSearchTerm.current) {
            setLoadingData(true);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {

        const scrollHeader = () => {
            if (refHeaderTable.current != null) {
                refHeaderTable.current.style.transform = `translateX(-${refOverflowTable.current.scrollLeft}px)`;
            }
        }

        const removeExistingListeners = () => {
            refOverflowTable.current?.removeEventListener("scroll", scrollHeader);
        }

        if (refOverflowTable.current && refHeaderScrollListener.current == null) {
            //Make sure we detach any existing listeners so its not applied twice
            removeExistingListeners();
            refOverflowTable.current.addEventListener("scroll", scrollHeader);
        }

    }, [refOverflowTable.current]);



    useEffect(() => {
        if (!loadingData) {
            dispatch(SetFiltering(jobLocationReduxName,
                {
                    list: selectedLocations
                }
            ));
        }
    }, [selectedLocations]);

    useEffect(() => {
        //We know the redux store has loaded now
        setDates();
    }, [reduxDates]);

    useEffect(() => {
        //We only want to set the locations once, else it gets stuck in a loop
        if (!locationSetUp && locations && locations.location && locations.location.length > 0) {
            setupLocationInfo();
        }
    }, [reduxLocations, locations]);

    useEffect(() => {
        //We know the redux store has loaded now
        if (reduxSearch != null && reduxSearch.length > 0 && reduxSearch !== searchTerm) {
            setSearchTerm(reduxSearch);
        }
    }, [reduxSearch]);

    function loadContractScreenData() {
        const jobsByContractsRequest = {
            filters: getFilters()
        };
        JobService.getJobsByContracts(jobsByContractsRequest).then(r => {
            if (r != null) {
                setJobsByContract(r);
                resetJobSelection();
                setUpdatedJobs([])
                reloadDone();
            }
        }, function () {
            NotificationToaster.show(Intent.DANGER, "Jobs By Contract could not be loaded. Please try loading the page again.");
            setLoadingData(false);
        });
    }

    function onLocationSelect(contractId, contractLineId, r, locationsValue) {

        var lastLocation = locationsValue.length > 0 ? locationsValue.pop() : null;
        var contract = jobsByContract.filter(j => j.contractId === contractId);
        var contractLine = contract[0].contractLines.filter(j => j.contractLineId === contractLineId);
        var job = contractLine[0].jobs.filter(j => j.jobId === r.jobId);
        job[0].locationId = lastLocation;

        updateChangedJobs(job[0], "locationId", lastLocation);
    }

    function onDueDateChange(contractId, contractLineId, r, duedate) {
        var contract = jobsByContract.filter(j => j.contractId === contractId);
        var contractLine = contract[0].contractLines.filter(j => j.contractLineId === contractLineId);
        var job = contractLine[0].jobs.filter(j => j.jobId === r.jobId);
        job[0].dueDate = duedate ? moment(duedate).format("YYYY-MM-DD") + ' 00:00:00' : null;
        job[0].locationId = job[0].locationId === 0 ? null : job[0].locationId;
        updateChangedJobs(job[0], "dueDate", duedate);
    }
    function updateChangedJobs(job, field, value) {

        var cloned= [
            ...updatedJobs
        ];

        var updatedjob = cloned.find(b => b.jobId === job.jobId);
        if (updatedjob) {
            updatedjob[field] = value;
        } else {
            var newJob = { "jobId": job.jobId, "locationId" : job.locationId, "dueDate": job.dueDate };
            newJob[field] = value;
            cloned.push(newJob);
        }

        setUpdatedJobs(cloned);
    }

    function mapContractRows(r, selectedJobsToMap, selectedJobsToCancelToMap) {
        var clonedRows = HelperFunctions.deepClone(r);
        if (clonedRows != null) {
            var data = [];
            
            // Get saved expanded state only if we haven't applied it yet
            const savedExpandedState = !hasAppliedSavedState ? restoreExpandedState() : null;
            
            // If we have saved state, restore it to the ref
            if (savedExpandedState && !hasAppliedSavedState) {
                savedExpandedState.contracts.forEach(savedContract => {
                    expandedStateRef.current[savedContract.contractId] = {
                        expanded: savedContract.expanded,
                        contractLines: {}
                    };
                    savedContract.contractLines.forEach(savedContractLine => {
                        expandedStateRef.current[savedContract.contractId].contractLines[savedContractLine.contractLineId] = savedContractLine.expanded;
                    });
                });
            }
            
            data = clonedRows.map((d) => {

                var cex = contracts.filter(c => c.contractId === d.contractId);
                
                // Use ref data if available, otherwise preserve existing state
                const refData = expandedStateRef.current[d.contractId];
                if (refData && refData.expanded !== undefined) {
                    d.expanded = refData.expanded;
                } else if (savedExpandedState) {
                    const savedContract = savedExpandedState.contracts.find(sc => sc.contractId === d.contractId);
                    d.expanded = savedContract ? savedContract.expanded : false;
                } else {
                    // Preserve existing expanded state from current contracts if it exists
                    d.expanded = cex.length > 0 ? cex[0].expanded : false;
                }

                d.contractLines.map((cl) => {

                    // Use ref data if available, otherwise use saved or existing state
                    if (refData && refData.contractLines && refData.contractLines[cl.contractLineId] !== undefined) {
                        cl.expanded = refData.contractLines[cl.contractLineId];
                    } else if (savedExpandedState) {
                        const savedContract = savedExpandedState.contracts.find(sc => sc.contractId === d.contractId);
                        if (savedContract) {
                            const savedContractLine = savedContract.contractLines.find(scl => scl.contractLineId === cl.contractLineId);
                            cl.expanded = savedContractLine ? savedContractLine.expanded : false;
                        }
                    } else if (cex.length > 0) {
                        // Preserve existing expanded state from current contract lines if it exists
                        var clex = cex[0].contractLines.filter(clf => clf.contractLineId === cl.contractLineId);
                        cl.expanded = clex.length > 0 ? clex[0].expanded : false;
                    } else {
                        cl.expanded = false;
                    }
                    
                    cl.jobs =
                        cl.jobs.map((j) => {

                            let row = [
                                <Link to={`/job/${j.jobId}?from=contracts`} key={`job-link-${j.jobId}`} onClick={() => handleJobLinkClick(j.jobId)}>{j.jobId}</Link>,
                                j.productName];

                            if (j.state === 'Awaiting Allocation') {
                                row.push(<FormLocationSelector
                                    businessArea={""}
                                    loading={loadingData && loading}
                                    selectedLocations={[j.locationId]}
                                    setSelectedLocations={(ev) => { onLocationSelect(d.contractId, cl.contractLineId, j, ev) }}
                                    useHierarchy={false}
                                    useLocationDefaults={false}
                                    locationDefaultsToTrue={true}
                                    customLocations={locationListCV}
                                    useCustomLocations={true}
                                    singleSelection
                                    key={`job-location-link-${j.jobId}`}
                                />,
                                    <FormDateInput
                                        id="policy-start-date"
                                        onChange={(ev) => { onDueDateChange(d.contractId, cl.contractLineId, j, ev) }}
                                        value={j.dueDate}
                                        minDate={moment("2000-01-01").toDate()}
                                    ></FormDateInput>
                                );
                            }
                            else {
                                row.push(j.locationName, <DateToLocal format='DD/MM/YYYY' key={`due-date-${j.jobId}`}>{j.dueDate}</DateToLocal>);
                            }

                            row.push(
                                <DateToLocal format='DD/MM/YYYY' key={`job-date-${j.jobId}`}>{j.scheduledDate}</DateToLocal>,
                                j.state,
                                "£" + parseFloat(cl.unitPrice).toFixed(2));

        
                            if (j.state === 'Awaiting Allocation') {
                                row.push(<FormCheckbox checked={selectedJobsToMap.includes(j.jobId.toString())} onChange={(x) => toggleSelectRow(x)} key={`${selectedJobIdPrefix}${j.jobId}`} id={`${selectedJobIdPrefix}${j.jobId}`} />);
                            }
                            else {
                                row.push(<Fragment></Fragment>,)
                            }

                            if (JobHelperFunctions.canCancel(j)) {
                                row.push(<FormCheckbox checked={selectedJobsToCancelToMap.includes(j.jobId.toString())} onChange={(x) => toggleSelectRowToCancel(x)} key={`${selectedJobIdPrefix}${j.jobId}`} id={`${selectedJobIdPrefix}${j.jobId}`} />);
                            }
                            else {
                                row.push(<Fragment></Fragment>,)
                            }

                             return row;
                        })
                    
                    return cl;
                });
                return d;
                
            });
            setLoadingData(false);
            setContracts(data);
            
            // Clear saved expanded state and set flag if we applied saved state
            if (savedExpandedState && !hasAppliedSavedState) {
                sessionStorage.removeItem('contractsExpandedState');
                setHasAppliedSavedState(true);
            }
        }
    }

    function resetJobSelection() {
        setSelectedJobsToEdit([]);
        setSelectedContractLines([]);
        setSelectedJobsToCancel([]);
        setSelectedContractLinesToCancel([]);

    }
    function closeCancelModal(changesMade) {
        setShowBulkCancelModal(false);
        if (changesMade) {
            loadContractScreenData();
            reloadRequired(0);
        }
    }

    function resetCurrent() {
        currentPageNumber.current = null;
        currentPageSize.current = null;
        currentSearchTerm.current = null;
        currentTableSortBy.current = null;
        currentTableSortDir.current = null;
        currentFilters.current = null;
        currentStartDate.current = null;
        currentEndDate.current = null;
    }

    function setUpPage() {
        resetCurrent();

        //This is either Internal jobs admin or upcoming Visits
        dispatch({ type: 'SITE_FULL_WIDTH' });
    }

    function setUpFilters() {
        setPageFilters([filters[0], filters[1], filters[2]]);
        
    }

    function setDates() {
        if (reduxDates && reduxDates.startDate !== null && reduxDates.endDate !== null) {
            setStartDate(moment(reduxDates.startDate).format("YYYY-MM-DD"));
            setEndDate(moment(reduxDates.endDate).format("YYYY-MM-DD"));
        } else {
            setStartDate(null);
            setEndDate(null);
        }
        setDateSetUp(true);
        setLoadingData(true);
    }

    function setupLocationInfo() {

            var clonedlocations = HelperFunctions.deepClone(locations);
            var clonedlocations1 = HelperFunctions.deepClone(locations);
            setLocationListCV(clonedlocations1);

            setBulkEditLocationList(clonedlocations);
            setLocationSetUp(true);
            if (reduxLocations != null && reduxLocations.list.length > 0) {
                setSelectedLocations(reduxLocations.list);
            } else {
                setSelectedLocations(clonedlocations1.location[0].items.map(l => l.id));
            }
        setLoadingData(true);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function closeBulkEditModal(response){
        setShowBulkEditModal(false);

        if (!response.isActivated && !response.isUpdated) {
            return;
        }

        loadContractScreenData();
        reloadRequired(0);
    }

    function onFilterChange(listingFilters) {
        setLoadingData(true);
        setAppliedFilters(listingFilters);
    }

    function onDateRangeChange(item) {
        if ((item?.startDate?.date != null && item?.endDate?.date != null) || (item?.startDate?.date == null && item?.endDate?.date == null)) {

            //Using the redux store should mean the setDates is called
            dispatch(SetFiltering(dateFilterReduxName, {
                startDate: item.startDate?.date,
                endDate: item.endDate?.date
            }));
        }
    }

    function onLocationListOpened() {
        locationListOnFilterOpen.current = selectedLocations;
    }

    function onLocationListClose() {
        if (locationListOnFilterOpen.current.toString() !== selectedLocations.toString()) {
            setLoadingData(true);
        }
    }

    function focusOnTextBox(InputRef) {
        setTimeout(function () {
            if (InputRef.current != null) {
                InputRef.current.focus();
            }
        });
    }

    function toggleSelectAllContractViewRow(e, jobs) {
        let { id, checked } = e.target;
        var cid = id.substring(selectedContractLinesIdPrefix.length);

        var jobIds = jobs
            .filter(x => x[statusColumn] === 'Awaiting Allocation')
            .map(j => j[0].props.children.toString());

        onContractLineCheckChange(cid, checked, jobIds);
    }

    function toggleSelectAllCancelContractViewRow(e, jobs) {
        let { id, checked } = e.target;
        var cid_clId = id.substring(selectedContractLinesToCancelIdPrefix.length);
        var idx = cid_clId.indexOf('%');
        var clid = parseInt(cid_clId.substring(idx + 1));
        var cid = parseInt(cid_clId.substring(0, idx));

        var contract = jobsByContract.filter(x => x.contractId === cid);

        if (contract.length === 0) {
            return;
        }

        var contractLines = contract[0].contractLines.filter(cl => cl.contractLineId === clid);

        if (contractLines.length === 0) {
            return;
        }

        var jobIds = contractLines[0].jobs
            .filter(j => JobHelperFunctions.canCancel(j))
            .map(j => j.jobId.toString());

        onContractLineCancelCheckChange(clid.toString(), checked, jobIds);
    }

    function toggleSelectRow(e) {
        let { id, checked } = e.target;
        var jId = id.substring(selectedJobIdPrefix.length);
        onJobCheckChange(jId, checked);
    }
    
    function onJobCheckChange(jobId, checked) {
        var clonedSelectedJobs = [
            ...selectedJobsToEdit
        ];

        //Adding or removing the activity from the selected activities list
        if (checked) {
            if (clonedSelectedJobs.indexOf(jobId) < 0) {
                clonedSelectedJobs = [...clonedSelectedJobs, jobId];
            }
        }
        else {
            clonedSelectedJobs = clonedSelectedJobs.filter(j => j !== jobId); 
        }
        setSelectedJobsToEdit(clonedSelectedJobs);
    }

    function toggleSelectRowToCancel(e) {
        let { id, checked } = e.target;
        var jId = id.substring(selectedJobIdPrefix.length);
        onJobToCancelCheckChange(jId, checked);
    }

    function onJobToCancelCheckChange(jobId, checked) {
        var clonedSelectedJobsToCancel = [
            ...selectedJobsToCancel
        ];

        //Adding or removing the activity from the selected activities list
        if (checked) {
            if (clonedSelectedJobsToCancel.indexOf(jobId) < 0) {
                clonedSelectedJobsToCancel = [...clonedSelectedJobsToCancel, jobId];
            }
        }
        else {
            clonedSelectedJobsToCancel = clonedSelectedJobsToCancel.filter(j => j !== jobId);
        }
        setSelectedJobsToCancel(clonedSelectedJobsToCancel);
    }
    function onContractLineCheckChange(contractLineId, checked, jobIds) {
        var selectedContractLineTempList = [
            ...selectedContractLines
        ];

        var selectedjobsTempList = [
            ...selectedJobsToEdit
        ];


        //Adding or removing the activity from the selected activities list
        if (checked) {
            if (selectedContractLineTempList.indexOf(contractLineId) < 0) {
                selectedContractLineTempList = [...selectedContractLineTempList, contractLineId];
            }
            selectedjobsTempList = [...selectedJobsToEdit, ...jobIds];
        }
        else {
            selectedContractLineTempList = selectedContractLines.splice(selectedContractLineTempList.indexOf(contractLineId), 1);
            selectedContractLineTempList = selectedContractLineTempList.filter(cl => cl !== contractLineId);
            selectedjobsTempList = selectedjobsTempList.filter(j => !jobIds.some(j1 => j1.jobId === j.jobId));
        }

        setSelectedContractLines(selectedContractLineTempList);
        setSelectedJobsToEdit(selectedjobsTempList);
    }

    function onContractLineCancelCheckChange(contractLineId, checked, jobIds) {
        var selectedContractLineTempList = [
            ...selectedContractLinesToCancel
        ];

        var selectedjobsTempList = [
            ...selectedJobsToCancel
        ];


        //Adding or removing the activity from the selected activities list
        if (checked) {
            if (selectedContractLineTempList.indexOf(contractLineId) < 0) {
                selectedContractLineTempList = [...selectedContractLineTempList, contractLineId];
            }

            selectedjobsTempList = [...selectedJobsToEdit, ...jobIds];
        }
        else {
            selectedContractLineTempList = selectedContractLines.splice(selectedContractLineTempList.indexOf(contractLineId), 1);
            selectedContractLineTempList = selectedContractLineTempList.filter(cl => cl !== contractLineId);
            selectedjobsTempList = selectedjobsTempList.filter(j => !jobIds.some(j1 => j1.jobId === j.jobId));
        }

        setSelectedContractLinesToCancel(selectedContractLineTempList);
        setSelectedJobsToCancel(selectedjobsTempList);
    }
    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        dispatch(SetFiltering(jobSearchReduxName, item.target.value));
    }

    function onResetJobCreationError(e, contractId, contractLineId) {
        e.stopPropagation();
        JobService.resetJobCreateError(contractLineId).then(function () {
            clearJobCreateError(contractId, contractLineId);
            NotificationToaster.show(Intent.SUCCESS, `Job creation will now re-process`);
        },
            (error) => {
                NotificationToaster.show(Intent.DANGER, error);
            }
        );
        
    }
    function clearJobCreateError(contractId, contractLineId) {
        const newArr = contracts.map(obj => {
            if (obj.contractId === contractId) {

                const newContractLines = obj.contractLines.map(obj1 => {
                    if (obj1.contractLineId === contractLineId) {
                        return { ...obj1, hasCreateJobError: false, createJobError: "" };
                    }
                    return obj1;
                });
                return { ...obj, contractLines: newContractLines };
            }

            return obj;
        });

        setContracts(newArr);
    }

    function getFilters() {
        let mappedFilters = appliedFilters.map(function (f) {
            return {
                key: f.name,
                ...f
            }
        });

        let filtersArray = [...mappedFilters,
        { "key": "Search", "value": searchTerm }
        ];

        if (startDate && endDate) {
            filtersArray = [...filtersArray,
            { "key": "CompletedScheduledStartDate", "value": startDate },
            { "key": "CompletedScheduledEndDate", "value": endDate }
            ];
        }

        if (selectedLocations != null) {
            selectedLocations.forEach(function (l) {
                filtersArray = [...filtersArray,
                { "key": "Location", "value": l }
                ];
            });
        }

        return filtersArray;
    }

    function onCollapsableClick(cIndex, clIndex) {
        var clonedContracts = [
            ...contracts
        ]

        var contract = clonedContracts[cIndex];

        if (contract) {
            if (clIndex !== undefined) {
                var contractLine = contract.contractLines[clIndex];
                if (contractLine) {
                    contractLine.expanded = !contractLine.expanded;
                    
                    // Store in ref for persistence
                    if (!expandedStateRef.current[contract.contractId]) {
                        expandedStateRef.current[contract.contractId] = { contractLines: {} };
                    }
                    expandedStateRef.current[contract.contractId].contractLines[contractLine.contractLineId] = contractLine.expanded;
                }
            } else {
                contract.expanded = !contract.expanded;
                
                // Store in ref for persistence
                if (!expandedStateRef.current[contract.contractId]) {
                    expandedStateRef.current[contract.contractId] = { contractLines: {} };
                }
                expandedStateRef.current[contract.contractId].expanded = contract.expanded;
            }
        }

        setContracts(clonedContracts);
        
        // Save expanded state immediately when it changes (using ref data)
        const expandedState = {
            contracts: clonedContracts.map(contractItem => {
                const refData = expandedStateRef.current[contractItem.contractId] || {};
                return {
                    contractId: contractItem.contractId,
                    expanded: refData.expanded || false,
                    contractLines: contractItem.contractLines.map(cl => ({
                        contractLineId: cl.contractLineId,
                        expanded: (refData.contractLines && refData.contractLines[cl.contractLineId]) || false
                    }))
                };
            })
        };
        sessionStorage.setItem('contractsExpandedState', JSON.stringify(expandedState));
    }

    function onSave() {
        setSaving(true);

        JobService.saveMultipleJobChanges(updatedJobs)
            .then(
                () => {
                    NotificationToaster.show(Intent.SUCCESS, "Jobs updated successfully.");
                    setSaving(false);
                    loadContractScreenData();
                },
                () => {
                    NotificationToaster.show(Intent.DANGER, "Could not update jobs. Please try again.");
                    setSaving(false);
                },
            );
    }

    function renderHeaderButton(contractId, contractLineId, isCreateJobError, errorMessage) {

        if (!isCreateJobError) {
            return <Fragment></Fragment>;
        }
        else {
            return (
                <Tooltip content={"Job creation errored" + (errorMessage !== "" ? " with error " + errorMessage : "") + " Click to re-process job creation for this contract line."} position={Position.TOP}>
                     <Button
                        onClick={(e) => {
                            onResetJobCreationError(e, contractId, contractLineId)
                        }}
                        text="Reset Create"
                        icon="reset"
                        iconOnly={true}
                        large={false}
                        intent="warning"
                    
                        minimal={true}>
                    </Button>               
                </Tooltip>
            );
        }

    }

    function getContractStatusClass(contractStatus) {
        if (!contractStatus) return '';
        
        const status = contractStatus.toLowerCase();
        if (status === 'live') {
            return 'contract-status-live';
        } else if (status === 'cancelling at next renewal') {
            return 'contract-status-in-query'; // Amber background
        } else if (status.includes('cancelled')) {
            return 'contract-status-cancelled';
        } else if (status === 'in-query' || status === 'in query') {
            return 'contract-status-in-query';
        }
        return '';
    }

    function getContractStatusIcon(contractStatus) {
        if (!contractStatus) return null;
        
        const status = contractStatus.toLowerCase();
        if (status === 'cancelling at next renewal' || status.includes('cancelled') || status === 'in-query' || status === 'in query') {
            return <Icon icon="warning-sign" className="contract-status-icon" size={16} />;
        }
        return null;
    }

    function getJobCountsByStatus(jobs) {
        const validJobs = jobs.filter(j => j[0] !== -1);
        const statusCounts = {};
        const JOB_STATUS_COLUMN_INDEX = 5; // Job status is in the 6th column (index 5)
        
        validJobs.forEach(job => {
            const status = job[JOB_STATUS_COLUMN_INDEX];
            if (status && typeof status === 'string') {
                statusCounts[status] = (statusCounts[status] || 0) + 1;
            }
        });
        
        // Format as "3 New, 2 Failed Appointment, 1 In Progress"
        const countStrings = Object.entries(statusCounts)
            .map(([status, count]) => `${count} ${status}`)
            .join(', ');
            
        return countStrings || '0 jobs';
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div className="row contained-content-job-contract">
                    <div className="contained-content-static">
                        <ShowHide
                            evaluator={!loadingData && pageFilters.length === 0}
                            hide={(
                                <Fragment>
                                    <div className="filter-controls">
                                        <div className="inline-items">
                                            <div className="inline-item fill">
                                                <div className="form-field-full">
                                                    <FormTextInput
                                                        maxLength={50}
                                                        inputRef={searchInputRef}
                                                        placeholder="Product, Reference or Contract"
                                                        onChange={onSearchChange}
                                                        value={searchTerm}
                                                        large
                                                        disabled={loadingData}
                                                        icon="search"
                                                        id="job-search-field" />
                                                </div>
                                            </div>
                                            <FormLocationSelector
                                                businessArea={""}
                                                loading={loadingData && loading}
                                                selectedLocations={selectedLocations}
                                                setSelectedLocations={setSelectedLocations}
                                                useHierarchy={false}
                                                useLocationDefaults={false}
                                                locationDefaultsToTrue={true}
                                                onLocationListClose={onLocationListClose}
                                                onLocationListOpened={onLocationListOpened}
                                                customLocations={locationListCV}
                                                useCustomLocations={true}
                                            />
                                            <FormDateRangeInput
                                                onChange={onDateRangeChange}
                                                startDate={startDate}
                                                endDate={endDate}
                                                disabled={loadingData}
                                                headingText={""}
                                                large
                                            ></FormDateRangeInput>

                                            <div key="act-list-bulk-edit" className="form-field">
                                                <Button text="Bulk Edit" onClick={() => { setShowBulkEditModal(true); }} disabled={selectedJobsToEdit.length === 0}></Button>
                                            </div>
                                            <div key="act-list-bulk-cancel" className="form-field">
                                                <Button text="Bulk Cancel" onClick={() => { setShowBulkCancelModal(true); }} disabled={selectedJobsToCancel.length === 0}></Button>
                                            </div>
                                            <div key="act-list-save" className="form-field">
                                                <ButtonSave text="Save" onClick={() => { onSave(); }} disabled={saving} simpleDisabled={updatedJobs.length === 0}></ButtonSave>
                                            </div>
                                            <div key="act-list-cancel" className="form-field">
                                                <Button text="Cancel" onClick={() => { loadContractScreenData(); }} disabled={updatedJobs.length === 0}></Button>
                                            </div>
                                            <Fragment>
                                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                            </Fragment>

                                            <AlertUnsavedData
                                                isDirty={updatedJobs.length > 0}>
                                            </AlertUnsavedData>
                                            
                                        </div>
                                        <Filter filterName="reviews" visible={showFilters} filters={pageFilters} onUpdate={onFilterChange} defaultFilterValues={defaultFilters} onClickOutside={onToggleFilter} />
                                    </div>
                                    
                                    <BulkEditJob jobs={selectedJobsToEdit} locations={bulkEditlocationList} loading={loadingData} showBulkEditModal={showBulkEditModal} closeBulkEditModal={closeBulkEditModal}></BulkEditJob>
                                </Fragment>
                            )}
                        />
                    </div>

                    <div className="contained-content-auto" ref={refOverflowTable}>
                        <Fragment>
                            <div className="clear-divide row" id="listing-job-contract">
                                {
                                    contracts.map((contract, cIndex) => {
                                        return (
                                                                        <Collapsable
                                key={"contract-" + contract.contractId}
                                loading={loadingData}
                                title={(
                                    <div className="inline-items">
                                        <span>{`Tacho - ${contract.contractNumber} - ${contract.contractName.replace('Contract - ', '')} (${contract.contractStatus ? contract.contractStatus : ''})`}</span>
                                        {getContractStatusIcon(contract.contractStatus)}
                                    </div>
                                )}
                                fullWidth={true}
                                expanded={contract.expanded}
                                lightMode={true}
                                onClick={() => onCollapsableClick(cIndex)}
                                className={getContractStatusClass(contract.contractStatus)}
                            >

                                                <div className="collapsable-content-lightMode">
                                                    {
                                                        contract.contractLines.map((cl, clIndex) => {
                                                            return (
                                                                <Collapsable
                                                                    key={"contractline-" + cl.contractLineId}
                                                                    loading={loadingData}
                                                                    title={(
                                                                        <div className="inline-items">
                                                                            <div>{`Tacho Contract Line - ${cl.jobs[0][1]} (${getJobCountsByStatus(cl.jobs)}) ${cl.contractLineDeleted ? ' (Deleted) ' : ''}`}</div>
                                                                            {cl.contractLineDeleted && <Icon icon="warning-sign" className="contract-status-icon" size={16} />}
                                                                            <div className="inline-item">&nbsp;</div>
                                                                        </div>
                                                                    )}
                                                                    fullWidth={true}
                                                                    expanded={cl.expanded}
                                                                    lightMode={true}
                                                                    onClick={() => onCollapsableClick(cIndex, clIndex)}
                                                                    headerButtons={renderHeaderButton(contract.contractId, cl.contractLineId, cl.hasCreateJobError, cl.createJobError)}
                                                                    className={cl.contractLineDeleted ? 'contract-line-deleted' : ''}
                                                                >
                                                                    <div className="collapsable-content-lightMode">
                                                                        <ListingTable
                                                                            id={"listing-table-contractline-" + cl.contractLineId}
                                                                            headers={["Job Id", "Product Name", "Location", "Due Date", "Scheduled Date", "Job Status", "Unit Price",
                                                                                <FormCheckbox checked={selectedContractLines.includes(cl.contractLineId.toString())} onChange={(x) => toggleSelectAllContractViewRow(x, cl.jobs)} key={`${selectedContractLinesIdPrefix}${cl.contractLineId}`} id={`${selectedContractLinesIdPrefix}${cl.contractLineId}`} label="EDIT" />,
                                                                                <FormCheckbox checked={selectedContractLinesToCancel.includes(cl.contractLineId.toString())} onChange={(x) => toggleSelectAllCancelContractViewRow(x, cl.jobs)} key={`${selectedContractLinesToCancelIdPrefix}${contract.contractId}%${cl.contractLineId}`} id={`${selectedContractLinesToCancelIdPrefix}${contract.contractId}%${cl.contractLineId}`} label="CANCEL" />]}
                                                                            data={cl.jobs.filter(j => j[0] !== -1)}
                                                                            totalRecordCount={cl.jobs.filter(j => j[0] !== -1).length}
                                                                            loadingData={loadingData}
                                                                            noDataMessage={noDataMessage}
                                                                            pageable={false}
                                                                        />
                                                                    </div>
                                                                    <div className="spacer-bottom">

                                                                    </div>

                                                                </Collapsable>
                                                            );
                                                        })
                                                    }
                                                </div>
                                            </Collapsable>
                                        );
                                    })
                                }
                                <CancelJob
                                    showCancelModal={showBulkCancelModal}
                                    closeCancelModal={closeCancelModal}
                                    jobIds={selectedJobsToCancel}
                                    loading={loadingData}
                                    showJobDetails={true}
                                />
                            </div>

                        </Fragment>
                    </div>


                    
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

JobListingContract.propTypes = {
    locations: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    filters: PropTypes.array,
    reloadDone: PropTypes.func,
    reloadRequired: PropTypes.func,
    reload: PropTypes.bool.isRequired,
};